'use client';

import * as React from 'react';

import { useServerAuth } from '@/auth';
import { createEventBus } from '@/shared/utils';
import { SeriesByID as SeriesDict } from '@/shared/i18n';

import { useRouter } from 'next/navigation';
import { SeriesByIdEventKeys, SeriesByIdEvents } from 'src/modules/catalog/series/events';
import { SeriesByIdScreen } from 'src/modules/catalog/series';
import { useSeriesByIdStore } from 'src/modules/catalog/series/store/series-by-id-store';
import { useFirebaseAuth } from 'src/shared/hooks/use-firebase-auth';
import { createCheckoutSession } from 'src/modules/catalog/series/utils/checkout-session';
import DetailsSummarySkeleton from 'src/shared/components/loading-skeleton/details-summary';
import DetailsLessonsSkeleton from 'src/shared/components/loading-skeleton/details-lesson';
import { sendGTMEvent } from 'src/shared/utils/gtm';

interface SeriesByIdContainerProps {
  dict: SeriesDict;
  seriesDocumentId: string;
  serieName: string;
}

function SeriesByIdContainer({ dict, seriesDocumentId, serieName }: SeriesByIdContainerProps) {
  const [accessToken, setAccessToken] = React.useState<string>();
  const seriesEventBus = createEventBus<SeriesByIdEvents>('series-by-id');
  const router = useRouter();
  const { user, hydrated } = useServerAuth();
  const resetStore = useSeriesByIdStore((state) => state.resetStore);
  const auth = useFirebaseAuth();

  const handleRedirect = React.useCallback(
    (params: { url: string; isExternalPage: boolean }) => {
      try {
        if (params.isExternalPage) {
          window.open(params.url, '_blank', 'noopener,noreferrer');
        } else {
          router.push(params.url);
        }
      } catch (error) {
        console.error('Invalid URL:', params.url, error);
      }
    },
    [router],
  );

  const handleBackNavigation = React.useCallback(() => {
    if (window.history.length > 1) {
      router.back();
    } else {
      router.push('/catalogo');
    }
  }, [router]);

  const handleCreateCheckoutSingleSeries = React.useCallback(
    ({
      seriesDocumentId,
      priceId,
      userUid,
    }: {
      seriesDocumentId: string;
      userUid: string;
      priceId: string;
    }) => {
      createCheckoutSession({
        seriesDocumentId,
        priceId,
        userUid,
        redirectUrl: window.location.href,
      });
      sendGTMEvent({
        event: 'begin_checkout',
        currency: 'eur',
        items: [{ id: seriesDocumentId, quantity: 1 }],
      });
    },
    [],
  );

  // const handleRedirect = React.useCallback((url: string) => router.push(url), [router]);

  React.useEffect(() => {
    const fetchToken = async () => {
      if (auth && auth.currentUser) {
        const token = await auth.currentUser.getIdToken(true);
        setAccessToken(token);
      }
    };
    // Redirect to login if user is not authenticated
    if (hydrated && !user?.email) {
      handleRedirect({ url: '/auth/login', isExternalPage: false });
    } else {
      fetchToken();
    }

    if (user?.email) {
      seriesEventBus.emit(SeriesByIdEventKeys.customer.view, { email: user.email });
    }

    seriesEventBus.on(SeriesByIdEventKeys.redirects.to, handleRedirect);
    seriesEventBus.on(SeriesByIdEventKeys.redirects.back, handleBackNavigation);
    seriesEventBus.on(
      SeriesByIdEventKeys.checkout.buySingleSeries,
      handleCreateCheckoutSingleSeries,
    );

    return () => {
      resetStore();
      seriesEventBus.off(SeriesByIdEventKeys.redirects.to, handleRedirect);
      seriesEventBus.off(SeriesByIdEventKeys.redirects.back, handleBackNavigation);
    };
  }, [seriesEventBus, user]);

  if (!hydrated || !auth) {
    return (
      <>
        <DetailsSummarySkeleton />
        <div className="max-w-[800px] m-auto">
          <div className="my-12">
            <DetailsLessonsSkeleton />
          </div>
        </div>
      </>
    );
  }

  return (
    <SeriesByIdScreen
      key="series-by-id"
      auth={auth}
      dict={dict}
      seriesDocumentId={seriesDocumentId}
      serieName={serieName}
      eventBus={seriesEventBus}
      accessToken={accessToken}
    />
  );
}

export default SeriesByIdContainer;
